import {
    collection,
    doc,
    getDoc,
    getDocs,
    limit as fbLimit,
    orderBy,
    query,
    startAfter,
    where,
    type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { FLIGHT_LOG_PAGE_SIZE, toDate, type FlightLog, type FlightLogCategory } from '@kaeliux/shared';
import { getSelectedUniverseId } from '../store/universeStore';
import { authedPost } from './http';

/**
 * Flight-log reads (real backend flavour) — direct Firestore query, not a
 * Cloud Function round trip: unlike planets/fleets, a flight log is an
 * immutable historical record written once server-side and needs no
 * replay/computation before being read, same rationale as
 * `ranking.service.ts`. Owner-only per `firestore.rules`' existing
 * `players/{userId}/{document=**}` wildcard — no rule changes were needed.
 * Mutating a report's saved state IS a write, so that goes through
 * `/saveBattleReport` instead (see `saveBattleReport` below).
 */

export type FlightLogCursor = QueryDocumentSnapshot | null;

export interface FlightLogPage {
    logs: FlightLog[];
    nextCursor: FlightLogCursor;
}

function normalizeFlightLog(id: string, data: Record<string, unknown>): FlightLog {
    const battleReport = data.battleReport as Record<string, unknown> | undefined;
    return {
        ...data,
        id,
        departedAt: toDate(data.departedAt, new Date(0)),
        occurredAt: toDate(data.occurredAt, new Date(0)),
        createdAt: toDate(data.createdAt, new Date(0)),
        // `battleReport.occurredAt` is a nested Timestamp — the top-level
        // spread above doesn't reach inside it, so convert it separately.
        ...(battleReport ? { battleReport: { ...battleReport, occurredAt: toDate(battleReport.occurredAt, new Date(0)) } } : {}),
    } as FlightLog;
}

/** One reverse-chronological page of a category's flight logs. */
export async function getFlightLogs(
    userId: string,
    category: FlightLogCategory,
    cursor: FlightLogCursor = null,
    pageSize: number = FLIGHT_LOG_PAGE_SIZE,
): Promise<FlightLogPage> {
    const q = query(
        collection(db, 'universes', getSelectedUniverseId(), 'players', userId, 'flightLogs'),
        where('category', '==', category),
        orderBy('occurredAt', 'desc'),
        ...(cursor ? [startAfter(cursor)] : []),
        fbLimit(pageSize),
    );
    const snap = await getDocs(q);
    return {
        logs: snap.docs.map((d) => normalizeFlightLog(d.id, d.data())),
        nextCursor: snap.docs.length === pageSize ? snap.docs[snap.docs.length - 1]! : null,
    };
}

/** Single-log lookup — used by `BattleReportPage` (deep-linkable by id). */
export async function getFlightLog(userId: string, logId: string): Promise<FlightLog | null> {
    const snap = await getDoc(doc(db, 'universes', getSelectedUniverseId(), 'players', userId, 'flightLogs', logId));
    if (!snap.exists()) return null;
    return normalizeFlightLog(snap.id, snap.data());
}

/** Toggle a battle report's saved state — admin-write-only doc, so this is a Cloud Function call, not a direct write. */
export async function saveBattleReport(logId: string, saved: boolean): Promise<{ logId: string; saved: boolean }> {
    return authedPost('/saveBattleReport', { logId, saved });
}
